import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useLocation } from "react-router-dom";
import Header from "../components/Header";
import ChatOrderInfo from "../components/ChatOrderInfo";
import ChatJobInfo from "../components/demand/ChatJobInfo";
import MessageList from "../components/chat/MessageList";
import Composer from "../components/chat/Composer";
import InternalNotes from "../components/chat/InternalNotes";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { Plus, NotebookPen } from "lucide-react";
import { toast } from "sonner";

const ROLE_COLOR = {
  owner: "text-[#D4AF37]",
  staff: "text-[#F2D98D]",
  supplier: "text-[#35C78A]",
  buyer: "text-[#A8A8AD]",
};

const sameList = (a, b) => a.length === b.length && a[a.length - 1]?.id === b[b.length - 1]?.id;

function ConvList({ convs, active, peer, onPick }) {
  return (
    <div className="flex-1 overflow-y-auto">
      {convs.map((c) => {
        const p = peer(c);
        return (
          <button key={c.id} data-testid={`conv-${c.id}`} onClick={() => onPick(c)}
            className={`w-full text-left p-3 border-b border-[#35353B] hover:bg-[#202127] transition-colors border-l-2 ${active?.id === c.id ? "bg-[#202127] border-l-[#D4AF37]" : "border-l-transparent"}`}>
            <div className="flex items-center justify-between gap-2">
              <div className="text-sm font-semibold text-[#F5F3ED] truncate">{p?.name}</div>
              <div className={`text-[10px] uppercase tracking-widest ${ROLE_COLOR[p?.role]}`}>{p?.role}</div>
            </div>
            <div className="text-xs text-[#A8A8AD] mt-1 line-clamp-1">{c.last_message || "—"}</div>
          </button>
        );
      })}
      {convs.length === 0 && <div className="p-6 text-center text-sm text-[#A8A8AD]">Belum ada percakapan</div>}
    </div>
  );
}

export default function Chat() {
  const { t } = useI18n();
  const { user } = useAuth();
  const isStaff = ["owner", "staff"].includes(user.role);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState({ conv: null, list: [] });
  const [contacts, setContacts] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [showList, setShowList] = useState(true);
  const [showNotes, setShowNotes] = useState(false);
  const [jump, setJump] = useState(0);
  const [params] = useSearchParams();
  const openId = params.get("c");
  const location = useLocation();
  useEffect(() => { if (!openId) { setShowList(true); setShowNotes(false); } }, [location.key]);

  const loadConvs = () => api.get("/conversations").then(r => setConvs(r.data));
  useEffect(() => { loadConvs(); api.get("/users/staff-contacts").then(r => setContacts(r.data.filter(c => c.id !== user.id))); }, []);
  const opened = useRef(null);
  useEffect(() => {
    if (!openId || opened.current === openId + location.key) { return; }
    const c = convs.find(x => x.id === openId);
    if (!c) { return; }
    opened.current = openId + location.key;
    setActive(c); setShowList(false); setJump((n) => n + 1);
  }, [openId, convs, location.key]);
  useEffect(() => { if (openId && !convs.some(x => x.id === openId)) { loadConvs(); } }, [openId, location.key]);

  const loadMsgs = (cid) => api.get(`/conversations/${cid}/messages`).then((r) => {
    setMsgs((cur) => (cur.conv === cid && sameList(cur.list, r.data) ? cur : { conv: cid, list: r.data }));
  });
  useEffect(() => {
    if (!active) { return undefined; }
    loadMsgs(active.id);
    const iv = setInterval(() => loadMsgs(active.id), 4000);
    return () => clearInterval(iv);
  }, [active?.id]);

  const onSent = async () => { await loadMsgs(active.id); setJump((n) => n + 1); loadConvs(); };

  const startWith = async (peerId) => {
    try {
      const r = await api.post("/conversations", { peer_id: peerId });
      await loadConvs();
      setActive(r.data);
      setShowNew(false);
      setShowList(false);
    } catch (err) { toast.error(err?.response?.data?.detail || "Tidak bisa memulai chat"); }
  };

  const peer = (c) => c.participants.find(p => p.id !== user.id);
  const activePeer = active && peer(active);

  return (
    <div className="min-h-screen bg-[#101114]">
      <Header />
      <div className="max-w-[1600px] mx-auto px-4 lg:px-8 py-6">
        <h1 className="text-3xl font-bold text-[#F5F3ED] mb-6" style={{fontFamily:"'Playfair Display', serif"}}>{t("nav.chat")}</h1>

        <div className="relative bg-[#191A1F] border border-[#35353B] rounded-lg overflow-hidden flex" style={{ height: "75vh" }}>
          <div className={`${showList || !active ? "flex" : "hidden"} md:flex w-full md:w-72 shrink-0 flex-col border-r border-[#35353B]`}>
            <div className="p-3 border-b border-[#35353B] flex items-center justify-between">
              <div className="text-sm font-bold text-[#F5F3ED]">Percakapan</div>
              <button data-testid="chat-new-btn" onClick={()=>setShowNew(!showNew)} className="p-1.5 bg-[#D4AF37] text-[#101114] hover:bg-[#F2D98D] rounded-sm"><Plus size={14} /></button>
            </div>
            {showNew && (
              <div className="p-3 border-b border-[#35353B] max-h-48 overflow-y-auto">
                <div className="text-xs text-[#A8A8AD] uppercase tracking-widest mb-2">Mulai percakapan</div>
                {contacts.map(c => (
                  <button key={c.id} data-testid={`new-chat-${c.id}`} onClick={()=>startWith(c.id)} className="w-full text-left p-2 hover:bg-[#202127] rounded-sm text-sm text-[#F5F3ED]">
                    {c.name} <span className={`text-xs ${ROLE_COLOR[c.role]}`}>· {c.role}</span>
                  </button>
                ))}
                {contacts.length === 0 && <div className="text-xs text-[#A8A8AD]">Tidak ada kontak</div>}
              </div>
            )}
            <ConvList convs={convs} active={active} peer={peer} onPick={(c) => { setActive(c); setShowList(false); setShowNotes(false); }} />
          </div>

          <div className={`${!showList && active ? "flex" : "hidden"} md:flex flex-1 min-w-0 flex-col`}>
            {active ? (
              <>
                <div className="p-3 border-b border-[#35353B] flex items-center gap-3">
                  <button className="md:hidden text-[#A8A8AD]" onClick={()=>setShowList(true)}>←</button>
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37] text-[#101114] flex items-center justify-center font-bold">{activePeer?.name?.[0]}</div>
                  <div className="min-w-0 flex-1">
                    <div data-testid="chat-peer-name" className="text-sm font-semibold text-[#F5F3ED] truncate">{activePeer?.name}</div>
                    <div className={`text-xs ${ROLE_COLOR[activePeer?.role]}`}>{activePeer?.role}</div>
                  </div>
                  {isStaff && <button data-testid="internal-notes-toggle" onClick={() => setShowNotes(!showNotes)} className="xl:hidden flex items-center gap-1.5 border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#101114] rounded-sm px-2.5 py-1.5 text-xs transition-colors"><NotebookPen size={14} /> Catatan</button>}
                </div>
                <ChatOrderInfo orderId={active.order_id} />
                <ChatJobInfo ctx={active.job_context} />
                <MessageList convId={active.id} msgs={msgs} userId={user.id} jumpSignal={jump} />
                <Composer key={active.id} conv={active} onSent={onSent} />
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-[#A8A8AD]">{t("chat.select")}</div>
            )}
          </div>

          {isStaff && activePeer && (
            <div className={`${showNotes ? "flex" : "hidden"} xl:flex absolute xl:static inset-y-0 right-0 z-20 w-full sm:w-80 shrink-0 border-l border-[#35353B] shadow-2xl xl:shadow-none`}>
              <InternalNotes key={activePeer.id} peer={activePeer} onClose={() => setShowNotes(false)} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
